export const createBookletPageOrder = (projects, fairDetails, opts = {}) => {
  const { flipMode = 'short', layoutMode = 'simple' } = opts;

  if (layoutMode !== 'imposed') {
    const sorted = [...projects].sort((a, b) =>
      (a.title || '').localeCompare(b.title || '')
    );
    // Reading order of the folded booklet. Padding goes just before the back cover
    // so the page count is a multiple of 4 without blanks interrupting the projects.
    const blank = { type: 'project', data: null, projectNumber: null };
    const readingOrder = [
      { type: 'cover', data: fairDetails },
      { type: 'welcome', data: fairDetails },
      { type: 'toc', data: sorted },
      ...sorted.map((project, i) => ({ type: 'project', data: project, projectNumber: i + 1 })),
    ];
    const totalPages = Math.ceil((readingOrder.length + 1) / 4) * 4;
    while (readingOrder.length < totalPages - 1) readingOrder.push({ ...blank });
    readingOrder.push({ type: 'thank-you', data: fairDetails });

    // Saddle-stitch imposition: each sheet carries a front spread then a back spread,
    // outermost sheet first.
    const pages = [];
    for (let sheet = 0; sheet < totalPages / 4; sheet += 1) {
      const outer = 2 * sheet;
      pages.push({
        pageIndex: pages.length,
        leftContent: readingOrder[totalPages - 1 - outer],
        rightContent: readingOrder[outer],
      });
      pages.push({
        pageIndex: pages.length,
        leftContent: readingOrder[outer + 1],
        rightContent: readingOrder[totalPages - 2 - outer],
      });
    }

    return pages;
  }

  // ---- original 'imposed' branch unchanged ----
  const sortedProjects = [...projects].sort((a, b) =>
    (a.title || '').localeCompare(b.title || '')
  );
  const totalPages = (() => {
    const remainingProjects = Math.max(0, sortedProjects.length - 2);
    let total = 1 + 1 + 1 + Math.ceil(remainingProjects / 2);
    if (total % 2 !== 0) total += 1;
    return total;
  })();

  const pages = [];

  pages.push({
    pageIndex: 0,
    leftContent:  { type: 'thank-you', data: fairDetails },
    rightContent: { type: 'cover',     data: fairDetails },
  });

  pages.push({
    pageIndex: 1,
    leftContent:  {
      type: 'project',
      data: sortedProjects.length ? sortedProjects[sortedProjects.length - 1] : null,
      projectNumber: sortedProjects.length || null,
    },
    rightContent: {
      type: 'welcome',
      data: fairDetails,
    },
  });

  pages.push({
    pageIndex: 2,
    leftContent:  { type: 'toc', data: sortedProjects },
    rightContent: {
      type: 'project',
      data: sortedProjects.length > 1 ? sortedProjects[sortedProjects.length - 2] : null,
      projectNumber: sortedProjects.length > 1 ? sortedProjects.length - 1 : null,
    },
  });

  let leftProjectIndex = sortedProjects.length - 3;
  let rightProjectIndex = 0;

  for (let i = 3; i < totalPages; i++) {
    const leftProject  = leftProjectIndex  >= 0 ? sortedProjects[leftProjectIndex]  : null;
    const rightProject = rightProjectIndex <  sortedProjects.length ? sortedProjects[rightProjectIndex] : null;

    pages.push({
      pageIndex: i,
      leftContent: {
        type: 'project',
        data: leftProject,
        projectNumber: leftProject ? leftProjectIndex + 1 : null,
      },
      rightContent: {
        type: 'project',
        data: rightProject,
        projectNumber: rightProject ? rightProjectIndex + 1 : null,
      }
    });

    leftProjectIndex--;
    rightProjectIndex++;
  }

  return padToEvenWithBlank(pages, flipMode);
};

/**
 * Pads the pages array to ensure even number of pages with blank pages
 * @param {Array} pages - Array of page objects
 * @param {string} flipMode - 'short' or 'long'
 * @returns {Array} Padded pages array
 */
export const padToEvenWithBlank = (pages, flipMode = 'short') => {
  // Ensure we have an even number of pages for proper printing
  if (pages.length % 2 !== 0) {
    const blankPageIndex = pages.length;
    pages.push({
      pageIndex: blankPageIndex,
      leftContent: {
        type: 'project',
        data: null,
        projectNumber: null,
      },
      rightContent: {
        type: 'project', 
        data: null,
        projectNumber: null,
      }
    });
  }
  return pages;
};
